import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Typography,
  alpha,
  Modal,
  Button,
  Card,
  CardMedia
} from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Close,
  Collections,
  ZoomIn
} from '@mui/icons-material';

interface CafePhotosProps {
  photos: string[];
  cafeName: string;
  darkMode: boolean;
  maxVisible?: number;
}

const CafePhotos: React.FC<CafePhotosProps> = ({ 
  photos, 
  cafeName, 
  darkMode, 
  maxVisible = 3 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [openModal, setOpenModal] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);

  if (!photos || photos.length === 0) return null;

  const goldColor = '#C4B66F';
  const visiblePhotos = photos.slice(0, maxVisible);

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % photos.length);
  };

  const openPhotoModal = (index: number) => {
    setModalIndex(index);
    setOpenModal(true);
  };

  const handleModalPrev = () => {
    setModalIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  const handleModalNext = () => {
    setModalIndex((prev) => (prev + 1) % photos.length);
  };

  return (
    <>
      {/* Photo Gallery */}
      <Box sx={{ mt: 3 }}>
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          mb: 1.5 
        }}>
          <Typography variant="subtitle2" sx={{ 
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            color: darkMode ? alpha('#fff', 0.8) : alpha('#051814', 0.8),
            fontWeight: 600 
          }}>
            <Collections fontSize="small" />
            Photos ({photos.length})
          </Typography>
          
          {photos.length > maxVisible && (
            <Button
              size="small"
              onClick={() => openPhotoModal(0)}
              sx={{
                color: goldColor,
                fontSize: '0.75rem',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: alpha(goldColor, 0.1)
                }
              }}
            >
              View All
            </Button>
          )}
        </Box>

        {/* Carousel for multiple photos */}
        {photos.length > 1 ? (
          <Box sx={{ position: 'relative' }}>
            <Box
              component="img"
              src={photos[currentIndex]}
              alt={`${cafeName} - Photo ${currentIndex + 1}`}
              onClick={() => openPhotoModal(currentIndex)}
              sx={{
                width: '100%',
                height: 200,
                objectFit: 'cover',
                borderRadius: '12px',
                cursor: 'pointer',
                border: `1px solid ${alpha(darkMode ? goldColor : '#051814', 0.1)}`,
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'scale(1.02)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
                }
              }}
            />
            
            {/* Navigation Arrows */}
            <IconButton
              onClick={handlePrev}
              sx={{
                position: 'absolute',
                left: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
              }}
              size="small"
            >
              <ChevronLeft />
            </IconButton>
            
            <IconButton
              onClick={handleNext}
              sx={{
                position: 'absolute',
                right: 8,
                top: '50%',
                transform: 'translateY(-50%)',
                bgcolor: 'rgba(0,0,0,0.5)',
                color: 'white',
                '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
              }}
              size="small"
            >
              <ChevronRight />
            </IconButton>
            
            {/* Photo Counter */}
            <Box sx={{ 
              position: 'absolute', 
              bottom: 8, 
              right: 8,
              bgcolor: 'rgba(0,0,0,0.6)',
              color: 'white',
              px: 1,
              py: 0.5,
              borderRadius: '4px'
            }}>
              <Typography variant="caption">
                {currentIndex + 1} / {photos.length}
              </Typography>
            </Box>
          </Box>
        ) : (
          // Single photo display
          <Box
            component="img"
            src={photos[0]}
            alt={cafeName}
            onClick={() => openPhotoModal(0)}
            sx={{
              width: '100%',
              height: 200,
              objectFit: 'cover',
              borderRadius: '12px',
              cursor: 'pointer',
              border: `1px solid ${alpha(darkMode ? goldColor : '#051814', 0.1)}`,
              transition: 'transform 0.2s',
              '&:hover': {
                transform: 'scale(1.02)',
                boxShadow: '0 8px 25px rgba(0,0,0,0.15)'
              }
            }}
          />
        )}
      </Box>

      {/* Photo Modal */}
      <Modal
        open={openModal}
        onClose={() => setOpenModal(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2
        }}
      >
        <Box sx={{ 
          position: 'relative',
          maxWidth: '90vw',
          maxHeight: '90vh',
          outline: 'none'
        }}>
          <IconButton
            onClick={() => setOpenModal(false)}
            sx={{
              position: 'absolute',
              right: -50,
              top: 0,
              color: 'white',
              bgcolor: 'rgba(0,0,0,0.5)',
              zIndex: 10,
              '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
            }}
          >
            <Close />
          </IconButton>
          
          <Box
            component="img"
            src={photos[modalIndex]}
            alt={`${cafeName} - Photo ${modalIndex + 1}`}
            sx={{
              maxWidth: '100%',
              maxHeight: '90vh',
              objectFit: 'contain',
              borderRadius: '8px'
            }}
          />
          
          {photos.length > 1 && (
            <>
              <IconButton
                onClick={handleModalPrev}
                sx={{
                  position: 'absolute',
                  left: -50,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                }}
              >
                <ChevronLeft />
              </IconButton>
              
              <IconButton
                onClick={handleModalNext}
                sx={{
                  position: 'absolute',
                  right: -50,
                  top: '50%',
                  transform: 'translateY(-50%)',
                  color: 'white',
                  bgcolor: 'rgba(0,0,0,0.5)',
                  '&:hover': { bgcolor: 'rgba(0,0,0,0.7)' }
                }}
              >
                <ChevronRight />
              </IconButton>
              
              <Box sx={{ 
                position: 'absolute', 
                bottom: 20, 
                left: '50%',
                transform: 'translateX(-50%)',
                bgcolor: 'rgba(0,0,0,0.6)',
                color: 'white',
                px: 2,
                py: 1,
                borderRadius: '20px'
              }}>
                <Typography variant="body2">
                  {modalIndex + 1} / {photos.length}
                </Typography>
              </Box>
            </>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default CafePhotos;